import { useEffect, useRef, useState } from 'react';
import {
    RESEARCH_AREAS,
    JOIN_US,
    AREA_TAG_COLOR,
    RESEARCH_ROUTE_MAP,
    LATEST_ACTIVITIES,
} from '../../config/data';
import { Icon } from '../../components/Icons';
import { usePage, PAGES } from '../../context/PageContext';
import { t } from '../../utils/i18n';
import './index.css';

/* 把研究方向类别在卡片上以英文 chip 显示，更贴近 IQuestLab 截图的
   LANGUAGE / ROBOTICS / REINFORCEMENT LEARNING 那种"学术类别"感。 */
const AREA_CHIP_LABEL = {
    llm: 'LARGE MODELS',
    'medical-ai': 'MEDICAL AI',
    'life-science': 'LIFE SCIENCES',
    math: 'MATHEMATICS',
    power: 'POWER INTELLIGENCE',
};

export default function HomePage() {
    const { navigate } = usePage();
    useEffect(() => {
        console.log('[HomePage] setting title, REACT_APP_SITE =', process.env.REACT_APP_SITE);
        document.title = t('至知创新研究院 · The Infinite Quest', 'IQuest Research · The Infinite Quest');
    }, []);

    /* 所有研究方向作为滑动式卡片 */
    const allResearch = RESEARCH_AREAS;
    const latestActivities = LATEST_ACTIVITIES.slice(0, 4);

    /* 滑动卡片状态 */
    const sliderRef = useRef(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(true);

    const updateScrollButtons = () => {
        const el = sliderRef.current;
        if (!el) return;
        setCanScrollLeft(el.scrollLeft > 10);
        setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
    };

    const scrollSlider = (direction) => {
        const el = sliderRef.current;
        if (!el) return;
        const cardWidth = el.querySelector('.home-research-card')?.offsetWidth || 360;
        el.scrollBy({ left: direction * (cardWidth + 28), behavior: 'smooth' });
    };

    useEffect(() => {
        const el = sliderRef.current;
        if (!el) return;
        updateScrollButtons();
        el.addEventListener('scroll', updateScrollButtons, { passive: true });
        return () => el.removeEventListener('scroll', updateScrollButtons);
    });

    return (
        <div className="home-page">
            {/* ============ Hero ============ */}
            <section className="home-hero home-hero-dark">
                <div className="home-hero-bg" aria-hidden />
                <div className="container home-hero-inner">
                    <div className="home-hero-text">
                        <div className="eyebrow eyebrow-on-dark">
                            <span className="eyebrow-dot" aria-hidden />
                            iQuest Research
                        </div>
                        <h1 className="home-hero-title">
                            <span className="home-hero-title-line">
                                The Infinite{' '}
                                <span className="accent-text home-hero-title-accent">Quest</span>
                            </span>
                            <span className="home-hero-title-line">for Intelligence</span>
                        </h1>
                        <div className="home-hero-subtitle">{t('做更有价值的AI', 'AI delivers more')}</div>
                        <p className="home-hero-lead">
                            {t(
                                '至知创新研究院立足人工智能前沿，是由深厚产业智慧驱动的新型科研组织，以原创性底层算法与下一代 AI 架构攻坚为基石，破解尖端研究与规模化产业落地的产学研难题。',
                                'IQuest Research is a new, industry-driven research organization at the cutting edge of artificial intelligence. Rooted in proprietary foundational algorithms and next-generation AI architecture, we bridge the gap between breakthrough research and scalable industry deployment.'
                            )}
                        </p>
                        <div className="home-hero-actions">
                            <button
                                type="button"
                                className="btn btn-primary btn-large"
                                onClick={() => navigate(PAGES.RESEARCH)}
                            >
                                {t('探索研究', 'Explore Research')}
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
                                    <path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </button>
                            <button
                                type="button"
                                className="btn btn-outline-strong btn-large home-hero-secondary"
                                onClick={() => navigate(PAGES.UPDATES)}
                            >
                                <span>{t('最新进展', 'Latest Updates')}</span>
                                <svg className="btn-arrow" viewBox="0 0 16 16" fill="none" aria-hidden>
                                    <path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </button>
                        </div>
                        {/* 极简水平 inline strip 替代 Stats 区段 */}
                        <div className="home-hero-inline-stats" aria-label={t('研究院概览', 'Institute overview')}>
                            <span>{t('5 研究方向', '5 Research Areas')}</span>
                            <span className="dot" aria-hidden>·</span>
                            <span>{t('20+ 论文', '20+ Papers')}</span>
                            <span className="dot" aria-hidden>·</span>
                            <span>{t('50+ 研究员', '50+ Researchers')}</span>
                            <span className="dot" aria-hidden>·</span>
                            <span>{t('10+ 合作机构', '10+ Partners')}</span>
                        </div>
                    </div>

                    {/* 右侧：实时动态面板 —— 顶部带"星空插画 banner"（宫崎骏感） */}
                    <aside className="home-hero-panel fx-tilt-hover" aria-label={t('近期动态', 'Recent updates')}>
                        <div className="home-hero-panel-glow" aria-hidden />
                        <div className="home-hero-panel-illust" aria-hidden>
                            <svg viewBox="0 0 480 180" preserveAspectRatio="xMidYMid slice">
                                <defs>
                                    <linearGradient id="hp-sky" x1="0" x2="0" y1="0" y2="1">
                                        <stop offset="0%" stopColor="#0A1F44" />
                                        <stop offset="60%" stopColor="#04102B" />
                                        <stop offset="100%" stopColor="#02061A" />
                                    </linearGradient>
                                    <radialGradient id="hp-glow" cx="50%" cy="50%" r="50%">
                                        <stop offset="0%" stopColor="#FFB066" stopOpacity="0.9" />
                                        <stop offset="100%" stopColor="#FFB066" stopOpacity="0" />
                                    </radialGradient>
                                </defs>
                                <rect width="480" height="180" fill="url(#hp-sky)" />
                                {/* 大光晕主星 */}
                                <circle cx="360" cy="70" r="48" fill="url(#hp-glow)" />
                                <circle cx="360" cy="70" r="3" fill="#FFFFFF" />
                                {/* 散落小星 */}
                                <circle cx="80" cy="40" r="1.4" fill="#FFFFFF" opacity="0.85" />
                                <circle cx="140" cy="110" r="1" fill="#FFFFFF" opacity="0.7" />
                                <circle cx="220" cy="55" r="1.2" fill="#FFFFFF" opacity="0.8" />
                                <circle cx="280" cy="125" r="1.5" fill="#FFFFFF" opacity="0.85" />
                                <circle cx="60" cy="140" r="1" fill="#FFFFFF" opacity="0.6" />
                                <circle cx="180" cy="150" r="1.1" fill="#FFFFFF" opacity="0.7" />
                                <circle cx="420" cy="30" r="1" fill="#FFFFFF" opacity="0.55" />
                                <circle cx="240" cy="20" r="0.9" fill="#FFFFFF" opacity="0.55" />
                                {/* 山影 */}
                                <path
                                    d="M0 180 L 60 130 L 110 152 L 180 116 L 240 145 L 320 110 L 400 150 L 480 130 L 480 180 Z"
                                    fill="#02061A"
                                    opacity="0.9"
                                />
                                {/* 银河带 */}
                                <path
                                    d="M -20 80 Q 240 20 500 90"
                                    stroke="rgba(143,177,255,0.35)"
                                    strokeWidth="1"
                                    fill="none"
                                />
                            </svg>
                            <div className="home-hero-panel-illust-tag">Latest Research Dispatch</div>
                        </div>
                        <div className="home-hero-panel-header">
                            <div className="home-hero-panel-status">
                                <span className="hero-live-dot" aria-hidden />
                                <span className="hero-panel-title">{t('实时动态', 'Live Updates')}</span>
                            </div>
                            <span className="home-hero-panel-sub">{t('最新 4 条', 'Latest 4')}</span>
                        </div>

                        <ul className="home-hero-panel-list">
                            {latestActivities.map((item) => {
                                const isExternal = !!item.href;
                                const isInternal = !!item.articleId;
                                const TitleTag = isExternal ? 'a' : 'button';
                                const titleProps = isExternal
                                    ? { href: item.href, target: '_blank', rel: 'noreferrer' }
                                    : isInternal
                                    ? { type: 'button', onClick: () => navigate(PAGES.ARTICLE_DETAIL, { articleId: item.articleId }) }
                                    : { type: 'button', disabled: true };
                                return (
                                    <li className="hero-panel-item" key={item.id}>
                                        <div className={`hero-panel-item-marker ${item.tagColor}`} aria-hidden>
                                            <Icon name={item.statusIcon} width={14} height={14} />
                                        </div>
                                        <div className="hero-panel-item-body">
                                            <div className="hero-panel-item-meta">
                                                <span className={`hero-panel-tag ${item.tagColor}`}>{item.tagLabel}</span>
                                                <time className="hero-panel-item-date" dateTime={item.dateFull}>
                                                    {item.dateLabel}
                                                </time>
                                            </div>
                                            <TitleTag className="hero-panel-item-title" {...titleProps}>
                                                {item.title}
                                                {(isExternal || isInternal) && (
                                                    <Icon name="arrowRight" width={12} height={12} aria-hidden />
                                                )}
                                            </TitleTag>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>

                        <button
                            type="button"
                            className="home-hero-panel-cta"
                            onClick={() => navigate(PAGES.UPDATES)}
                        >
                            {t('查看全部动态', 'View All Updates')}
                            <Icon name="arrowRight" width={14} height={14} aria-hidden />
                        </button>
                    </aside>
                </div>
            </section>

            {/* ============ Latest Research —— IQuestLab 截图核心区段 ============ */}
            <section className="section home-research-section">
                <div className="container">
                    <div className="section-header-split">
                        <div className="section-header-text">
                            <div className="eyebrow">Latest Research</div>
                            <h2 className="section-title">
                                {t('重塑 AI 的 ', 'Redefining the ')}<span className="accent-text">Algorithmic</span>{t(' 边界', ' Frontier')}
                            </h2>
                            <p className="section-subtitle">
                                {t(
                                    '围绕大模型、医疗智能、生命科学、数学智能与数智电力五大研究方向，深耕基础算法与高复杂度场景落地，定义下一代 AI 能力边界。',
                                    'Centered on Large Models, Medical AI, Life Sciences, Mathematics, and Electric Power Intelligence, we go deep in foundational algorithm development and high\u2011complexity application deployment.'
                                )}
                            </p>
                        </div>
                        <button
                            type="button"
                            className="link-warm section-header-link"
                            onClick={() => navigate(PAGES.RESEARCH)}
                        >
                            {t('查看全部研究', 'View All Research')}
                            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden>
                                <path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                    </div>
                    <div className="home-research-slider-wrap">
                        {canScrollLeft && (
                            <button
                                type="button"
                                className="home-research-slider-btn home-research-slider-btn-left"
                                onClick={() => scrollSlider(-1)}
                                aria-label="Scroll left"
                            >
                                <svg width="20" height="20" viewBox="0 0 16 16" fill="none" aria-hidden>
                                    <path d="M10 3l-5 5 5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </button>
                        )}
                        <div className="home-research-slider" ref={sliderRef}>
                            {allResearch.map((area) => {
                                const to = RESEARCH_ROUTE_MAP[area.id] || '/research';
                                const colorClass = AREA_TAG_COLOR[area.id] || 'tag-blue';
                                const chipLabel = AREA_CHIP_LABEL[area.id] || (area.title || '').toUpperCase();
                                return (
                                    <article className="home-research-card fx-tilt-hover" key={area.id}>
                                        <div className="home-research-card-img-wrap">
                                            <span className={`home-research-card-chip ${colorClass}`}>{chipLabel}</span>
                                            <img src={area.img} alt={area.title} loading="lazy" decoding="async" />
                                        </div>
                                        <div className="home-research-card-body">
                                            <h3 className="home-research-card-title">{area.title}</h3>
                                            <p className="home-research-card-desc">{area.desc}</p>
                                            <button
                                                type="button"
                                                className="link-warm home-research-card-more"
                                                onClick={() => navigate(to)}
                                            >
                                                {t('阅读详情', 'Learn More')}
                                                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden>
                                                    <path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </button>
                                        </div>
                                    </article>
                                );
                            })}
                        </div>
                        {canScrollRight && (
                            <button
                                type="button"
                                className="home-research-slider-btn home-research-slider-btn-right"
                                onClick={() => scrollSlider(1)}
                                aria-label="Scroll right"
                            >
                                <svg width="20" height="20" viewBox="0 0 16 16" fill="none" aria-hidden>
                                    <path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </button>
                        )}
                    </div>
                </div>
            </section>

            {/* ============ CTA banner (深蓝档) ============ */}
            <section className="section home-cta-section">
                <div className="container">
                    <div className="home-cta-card home-cta-card-dark">
                        <div className="home-cta-text">
                            <div className="eyebrow eyebrow-on-dark">Careers · Join Us</div>
                            <h2>
                                {t('加入我们，与世界一起 ', 'Join us, take a step ')}<span className="accent-text">{t('向前一步', 'forward')}</span>
                            </h2>
                            <p>{t('我们正在持续招募大模型、生命科学、数学智能、医疗智能方向的顶尖人才。', 'We are continuously recruiting top talent in Large Models, Life Sciences, Mathematical AI, and Medical AI.')}</p>
                        </div>
                        <div className="home-cta-actions">
                            <a
                                className="btn btn-primary btn-large"
                                href={JOIN_US.href || 'mailto:hr@iquestlab.com'}
                                target={JOIN_US.href ? '_blank' : undefined}
                                rel="noreferrer"
                            >
                                {t('查看岗位', 'View Positions')}
                            </a>
                            <a className="btn btn-outline-strong btn-large" href="mailto:hr@iquestlab.com">
                                <span>{t('联系我们', 'Contact Us')}</span>
                                <svg className="btn-arrow" viewBox="0 0 16 16" fill="none" aria-hidden>
                                    <path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </a>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
